import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { BackendModuleKey } from "../lib/backendModuleKeys";
import { isBackendModuleKey } from "../lib/backendModuleKeys";
import type { PendingRequestRef } from "../lib/accessRequests";
import { normalizeAccessRequestList } from "../lib/accessRequests";
import { apiHelpers } from "../lib/api";
import { useAuthStore } from "../stores/authStore";
import { accessRequestQueryKeys } from "./useAccessRequests";

/** Pending module access requests for sidebar badges (single list fetch). */
export function useNavPendingModules() {
  const user = useAuthStore((s) => s.user);

  const query = useQuery({
    queryKey: accessRequestQueryKeys.mine("self"),
    enabled: Boolean(user),
    queryFn: async () => {
      const res = await apiHelpers.accessRequests.list({ scope: "mine" });
      return normalizeAccessRequestList(res.data);
    },
    staleTime: 30_000,
  });

  const pendingByModule = useMemo(() => {
    const map = new Map<BackendModuleKey, PendingRequestRef>();
    for (const record of query.data ?? []) {
      if (record.status !== "pending" || record.target_type !== "module") {
        continue;
      }
      if (!isBackendModuleKey(record.module_key)) {
        continue;
      }
      const key = record.module_key;
      map.set(key, {
        id: record.id,
        status: record.status,
        created_at: record.created_at,
      });
    }
    return map;
  }, [query.data]);

  return { pendingByModule, isLoading: query.isLoading };
}
